import type { Theme } from '@src/theme/theme';

export interface CssVariable {
  name: string;
  /** Resolved value for the theme currently on <html>, e.g. `#ff6960`. */
  value: string;
}

export interface TokenSnapshot {
  /** Which theme the values were resolved against. */
  theme: Theme;
  variables: CssVariable[];
}

/**
 * Collects every custom property the loaded stylesheets declare, in declaration
 * order.
 *
 * Reading the stylesheet rather than keeping a hand-written list in TypeScript is
 * the point: styles/tokens.css and the active package stay the only place a token
 * is defined, and a token added there appears on this page with no other edit.
 */
function declaredCustomProperties(): string[] {
  const names: string[] = [];
  const seen = new Set<string>();

  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList;
    try {
      rules = sheet.cssRules;
    } catch {
      // A cross-origin stylesheet (none of ours) — nothing readable here.
      continue;
    }

    for (const rule of Array.from(rules)) {
      if (!(rule instanceof CSSStyleRule)) {
        continue;
      }
      for (let index = 0; index < rule.style.length; index += 1) {
        const property = rule.style.item(index);
        if (property.startsWith('--') && !seen.has(property)) {
          seen.add(property);
          names.push(property);
        }
      }
    }
  }

  return names;
}

/** Read after the active package stylesheet has committed to the DOM. */
export function readCssVariables(theme: Theme): TokenSnapshot {
  const computed = getComputedStyle(document.documentElement);

  const variables = declaredCustomProperties()
    .map((name) => ({ name, value: computed.getPropertyValue(name).trim() }))
    .filter((variable) => variable.value !== '');

  return { theme, variables };
}
